/* eslint-disable no-underscore-dangle */
import { useState, useEffect, useMemo } from 'react';

import { useLocation } from 'react-router-dom';
import { HashLink as Link } from 'react-router-hash-link';

import { SVGAsset } from 'components/atoms/SVGAsset';
import { ModalAnchor } from 'components/templates/layouts/PageLayout/ModalAnchor';
import { useIsAuthenticated } from 'hooks/api/auth/useIsAuthenticated';

import { useImageOnLoad } from '../Image';
import { Typography } from '../Typography';
import {
    StyledSectionHeader,
    TitleWrapper,
    MobileButtonWrapper,
    MobileButton,
    AnchorsWrapper,
    AnchorButton,
    AnchorTitle,
    MobileButtonTitle,
} from './Header.styled';
import type { AnchorProps, SectionHeaderProps } from './types';

export const Anchor = (props: AnchorProps): JSX.Element => {
    const { title, anchor } = props;
    return (
        <Link to={`#${anchor}`}>
            <AnchorButton>
                <AnchorTitle>{title}</AnchorTitle>
            </AnchorButton>
        </Link>
    );
};

const setTitle = (title: string): string => {
    const textTitle = title.split(' ');
    textTitle[0] = textTitle[0].replaceAll(/[0-9]/g, '').replaceAll('.', '');
    const headerTitle = textTitle.join(' ');

    return headerTitle;
};

export const SectionHeader = (props: SectionHeaderProps): JSX.Element => {
    const { title, anchors, cover, file, coverUpdateHash } = props;

    const location = useLocation();
    const [isAdmin, setIsAdmin] = useState<boolean>();

    const { authChecked, authenticated } = useIsAuthenticated();

    const imagePathPrefix =
        authChecked && authenticated ? '/api/builder/image' : '/api/image';

    const coverSource = useMemo(
        () => `${imagePathPrefix}/${cover}/xs/4/?update_hash=${coverUpdateHash} 576w,
    ${imagePathPrefix}/${cover}/sm/4/?update_hash=${coverUpdateHash} 768w,
    ${imagePathPrefix}/${cover}/md/4/?update_hash=${coverUpdateHash} 992w,
    ${imagePathPrefix}/${cover}/lg/4/?update_hash=${coverUpdateHash} 1200w,
    ${imagePathPrefix}/${cover}/original/4/?update_hash=${coverUpdateHash} 1600w`,
        [cover, imagePathPrefix, coverUpdateHash]
    );

    const { handleImageOnLoad, css } = useImageOnLoad();

    useEffect(() => {
        if (location.pathname.includes('content_manager')) {
            setIsAdmin(true);
        } else {
            setIsAdmin(false);
        }
    }, [location.pathname]);

    const [thumbLoaded, setThumbLoaded] = useState(false);

    const anchorList = useMemo(() => {
        return (
            anchors?.map((el, index) => (
                <Anchor
                    key={index}
                    title={
                        el.component === 'section' ? el.props?.sectionTitle : ''
                    }
                    anchor={el._key}
                />
            )) || []
        );
    }, [anchors]);

    return (
        <StyledSectionHeader data-cover={!!cover}>
            {cover ? (
                <div className="cover_img_wrap">
                    <img
                        alt="thumbnail"
                        style={css.thumbnail}
                        src={`${imagePathPrefix}/${cover}/thumbnail/5/?update_hash=${coverUpdateHash}`}
                        className="cover_img"
                        onLoad={() => setThumbLoaded(true)}
                    />
                    <img
                        alt={title ? setTitle(title) : ''}
                        onLoad={handleImageOnLoad}
                        style={css.fullSize}
                        srcSet={coverSource}
                        className="cover_img"
                        sizes="100vw, 95vw, 95vw, 60vw, 60vw"
                    />
                </div>
            ) : undefined}
            <TitleWrapper
                isAdmin={isAdmin}
                emptyAnchors={anchorList.length === 0}
            >
                {title ? (
                    <Typography
                        variant="title.large"
                        className="content-title"
                        data-cover-loading={cover && !thumbLoaded}
                    >
                        {setTitle(title)}
                    </Typography>
                ) : undefined}

                {file && thumbLoaded ? (
                    <MobileButton className="file" href={file} target="_blank">
                        <SVGAsset src="icons.24px.pdf" />
                        <MobileButtonTitle>
                            Открыть .pdf раздела
                        </MobileButtonTitle>
                    </MobileButton>
                ) : undefined}
            </TitleWrapper>

            {thumbLoaded && anchorList.length > 1 ? (
                <MobileButtonWrapper isAdmin={isAdmin}>
                    <div className="anchors">
                        <ModalAnchor anchors={anchors} />
                    </div>
                </MobileButtonWrapper>
            ) : undefined}
            {thumbLoaded && anchorList.length > 1 ? (
                <AnchorsWrapper isAdmin={isAdmin}>{anchorList}</AnchorsWrapper>
            ) : undefined}
        </StyledSectionHeader>
    );
};
